import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchMedicalDepartments,
  fetchDoctorsByDepartment,
} from "../api/registrationService";

export function useAdmissionDoctors() {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentNames, setDepartmentNames] = useState([]);
  const [admittingDoctors, setAdmittingDoctors] = useState([]);
  const [doctorsByDeptMap, setDoctorsByDeptMap] = useState({});
  const doctorsByDeptMapRef = useRef({});
  const [loadingAdmittingDoctors, setLoadingAdmittingDoctors] = useState(false);
  const [loadingAttendingDoctors, setLoadingAttendingDoctors] = useState({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const { options, names } = await fetchMedicalDepartments();
        if (cancelled || !mountedRef.current) return;
        setDepartmentOptions(options);
        setDepartmentNames(names);
      } catch {
        if (cancelled || !mountedRef.current) return;
        setDepartmentOptions([]);
        setDepartmentNames([]);
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, []);

  const loadAdmittingDoctors = useCallback(async (department) => {
    if (!department || department === "Other/External") {
      if (mountedRef.current) setAdmittingDoctors([]);
      return;
    }
    if (mountedRef.current) setLoadingAdmittingDoctors(true);
    try {
      const doctors = await fetchDoctorsByDepartment(department);
      if (mountedRef.current) setAdmittingDoctors(doctors);
    } catch {
      if (mountedRef.current) setAdmittingDoctors([]);
    } finally {
      if (mountedRef.current) setLoadingAdmittingDoctors(false);
    }
  }, []);

  const fetchDoctorsForDept = useCallback(async (department) => {
    if (!department || department === "Other/External") return;
    if (doctorsByDeptMapRef.current[department]) return;

    if (mountedRef.current) {
      setLoadingAttendingDoctors((prev) => ({ ...prev, [department]: true }));
    }
    try {
      const doctors = await fetchDoctorsByDepartment(department);
      doctorsByDeptMapRef.current[department] = doctors;
      if (mountedRef.current) {
        setDoctorsByDeptMap((prev) => ({ ...prev, [department]: doctors }));
      }
    } catch {
      doctorsByDeptMapRef.current[department] = [];
      if (mountedRef.current) {
        setDoctorsByDeptMap((prev) => ({ ...prev, [department]: [] }));
      }
    } finally {
      if (mountedRef.current) {
        setLoadingAttendingDoctors((prev) => ({ ...prev, [department]: false }));
      }
    }
  }, []);

  return {
    departmentOptions,
    departmentNames,
    admittingDoctors,
    doctorsByDeptMap,
    loadingAdmittingDoctors,
    loadingAttendingDoctors,
    loadAdmittingDoctors,
    fetchDoctorsForDept,
  };
}
